import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

import Spinner from './Spinner';
import axios from 'axios';
import { useAuth } from '../AuthContext';

const resolveApiBase = () => {
  const baseEnv = process.env.REACT_APP_API_URL || '';
  const hostname = window.location.hostname;
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isLocalHost) return '';
  if (!baseEnv) return '';
  try {
    const envUrl = new URL(baseEnv);
    if (envUrl.origin === window.location.origin) return '';
    return baseEnv.replace(/\/$/, '');
  } catch {
    return baseEnv.replace(/\/$/, '');
  }
};

type Tile = {
  title: string;
  description: string;
  link: string;
  icon: string;     // caminho do ícone
  module: string;   // agrupador (ex.: "Configuração", "Ferramentas com IA")
};

type ModuleMeta = { name: string; description?: string };

const SPA_LINKS = new Set([
  '/home',
  '/users',
  '/profiles',
  '/integrations',
  '/global-clients',
  '/job-monitor',
  '/benchmark-perfis-sap',
  '/gerador-perfis',
  '/analisador-logs-sap',
  '/action-orglevel',
  '/action-risk',
  '/cowner-cockpit',
  '/cowner-admin-clientes',
  '/sap-controls-repository',
  '/ads',
]);

const isSpaLink = (path: string) => SPA_LINKS.has(path);

const canAccessTile = (tileLink: string, allowedLinks: string[] | null) => {
  if (allowedLinks === null) return false;
  if (allowedLinks.includes(tileLink)) return true;
  // Backward-compatible fallback: ADS follows existing Redesenho permissions
  if (tileLink === '/ads') {
    return allowedLinks.includes('/gerador-perfis') || allowedLinks.includes('/analisador-logs-sap');
  }
  return false;
};

// Local theme fallback for pages that still reference `theme`.
// `AppLayout` provides the global theme; keep a local copy to avoid build errors.
const getInitialTheme = () => (typeof window !== 'undefined' ? (localStorage.getItem('phoenixTheme') || 'light') : 'light');

/* ---------- Metadados dos módulos (ordem e descrição) ---------- */
export const MODULES: ModuleMeta[] = [
  { name: 'Configuração', description: 'Ajustes do sistema, acessos e integrações.' },
  { name: 'Ferramentas com IA', description: 'Recursos inteligentes para automação e análise.' },
  { name: 'Repositórios e modelos', description: 'Ferramentas para gerenciar repositórios e modelos de dados.' },
  { name: 'Redesenho de perfis', description: 'Ferramentas para projetos de redesenho de perfis' },
  { name: 'Compliance Owner - Tools', description: 'Ferramentas para gestão de clientes e compliance' },
];

/* ---------------------- Cadastre os tiles aqui ---------------------- */
export const TILES: Tile[] = [
  // Configuração
  { module: 'Configuração', title: 'Manutenção de Usuários', description: 'Gerencie usuários do sistema', link: '/users', icon: '/assets/img/icons/users' },
  { module: 'Configuração', title: 'Manutenção de Perfis', description: 'Defina perfis e permissões', link: '/profiles', icon: '/assets/img/icons/profiles' },
  { module: 'Configuração', title: 'Integrações', description: 'Configure conexões SAP (ECC/S4HANA)', link: '/integrations', icon: '/assets/img/icons/connections' },
  { module: 'Configuração', title: 'Clientes Globais', description: 'Cadastre e mantenha clientes globais da solução', link: '/global-clients', icon: '/assets/img/icons/globalclients' },
  { module: 'Configuração', title: 'Monitor de Jobs', description: 'Monitore execuções assíncronas e filas de processamento', link: '/job-monitor', icon: '/assets/img/icons/job-monitor' },
  { module: 'Configuração', title: 'Parâmetros Gerais', description: 'Ajuste parâmetros globais', link: '/settings', icon: '/assets/img/icons/settings' },

  // Ferramentas com IA
  { module: 'Ferramentas com IA', title: 'Chat bot', description: 'AI Assistant - Especialista TrustSis', link: '/ai/chatbot', icon: '/assets/img/icons/chatbot' },
  { module: 'Ferramentas com IA', title: 'Análise de log (Perfis SAP)', description: 'Insights automáticos sobre logs de acesso', link: '/ai/sap-logs', icon: '/assets/img/icons/sap-logs' },
  { module: 'Ferramentas com IA', title: 'Classificador de Acessos', description: 'Assessment de maturidade de acessos e segurança', link: '/ai/access-classifier', icon: '/assets/img/icons/access-classifier' },

  // Repositórios e modelos
  { module: 'Repositórios e modelos', title: 'Benchmark de Perfis SAP', description: 'Repositório benchmark de perfis de acesso SAP', link: '/benchmark-perfis-sap', icon: '/assets/img/icons/benchmark-perfis-sap' },
  { module: 'Repositórios e modelos', title: 'Ações x Nível organizacional', description: 'Repositório de relação de ações por nível organizacional', link: '/action-orglevel', icon: '/assets/img/icons/action-orglevel' },
  { module: 'Repositórios e modelos', title: 'Ações x Riscos (SoD/SAT)', description: 'Repositório de relação de ações e riscos (SoD/SAT)', link: '/action-risk', icon: '/assets/img/icons/action-risk' },
  { module: 'Repositórios e modelos', title: 'Repositório de Controles SAP', description: 'Controles de auditoria e compliance para ambientes SAP', link: '/sap-controls-repository', icon: '/assets/img/icons/sap-controls' },
  // Redesenho de perfis
  { module: 'Redesenho de perfis', title: 'Gerador de Perfis', description: 'Estruture perfis de acesso com base em dados do cliente e benchmark SAP', link: '/gerador-perfis', icon: '/assets/img/icons/profilegen' },
  { module: 'Redesenho de perfis', title: 'Analisador de logs SAP', description: 'Analise e consolide logs de autorização para carga em perfis de acesso', link: '/analisador-logs-sap', icon: '/assets/img/icons/log_analyzer' },
  { module: 'Redesenho de perfis', title: 'Access Design Solution', description: 'Ciclos de validação e aprovação do mapeamento de perfis com key users do cliente', link: '/ads', icon: '/assets/img/icons/profilegen' },
  
  // Compliance Owner - Tools
  { module: 'Compliance Owner - Tools', title: 'Administrar clientes', description: 'Administre cadastro e configurações de clientes do Compliance Owner', link: '/cowner-admin-clientes', icon: '/assets/img/icons/cowner-admin-clientes' },
  { module: 'Compliance Owner - Tools', title: 'Cockpit - Compliance Owner', description: 'Cockpit de ferramentas para o Compliance Owner', link: '/cowner-cockpit', icon: '/assets/img/icons/cowner-cockpit' },
];
/* ------------------------------------------------------------------- */


const Home: React.FC = () => {
  const [allowedLinks, setAllowedLinks] = useState<string[] | null>(null); // null = not loaded, [] = none allowed
  const [userFullName, setUserFullName] = useState<string | null>(null);
  const [lastLoginDisplay, setLastLoginDisplay] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState<string>(() => getInitialTheme());
  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail;
        if (detail === 'light' || detail === 'dark') setTheme(detail);
        else setTheme(getInitialTheme());
      } catch (err) {
        setTheme(getInitialTheme());
      }
    };
    window.addEventListener('phoenixThemeChange', handler as EventListener);
    return () => window.removeEventListener('phoenixThemeChange', handler as EventListener);
  }, []);
  // theme handled globally in AppLayout

  // load user profile and allowed apps using AuthContext (avoids extra /api/test-session call)
  const { userId } = useAuth();
  useEffect(() => {
    const urlBase = resolveApiBase();
    (async () => {
      try {
        if (!userId) { setAllowedLinks([]); setLoading(false); return; }
        const user = await axios.get(`${urlBase}/api/users/${userId}`, { withCredentials: true });
        const rawProfile = user.data?.profile;
        const profileId = typeof rawProfile === 'string' ? rawProfile : rawProfile?._id;
        // armazena nome do usuário para saudação
        setUserFullName(user.data?.fullName || user.data?.username || null);
        const lastLoginValue = user.data?.lastLoginAt ? new Date(user.data.lastLoginAt) : new Date();
        const resolvedLastLogin = Number.isNaN(lastLoginValue.getTime()) ? new Date() : lastLoginValue;
        setLastLoginDisplay(
          resolvedLastLogin.toLocaleString('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'medium'
          })
        );
        if (!profileId) { setAllowedLinks([]); setLoading(false); return; }
        const [appsRes, paRes] = await Promise.all([
          axios.get(`${urlBase}/api/applications`, { withCredentials: true }),
          axios.get(`${urlBase}/api/profile-apps`, { withCredentials: true })
        ]);
        const apps: any[] = Array.isArray(appsRes.data) ? appsRes.data : [];
        const links: any[] = Array.isArray(paRes.data) ? paRes.data : [];
        // applications.name should match tile.link
        const allowedAppIds = links
          .filter((l) => {
            const linkProfileId = typeof l.profile === 'string' ? l.profile : l.profile?._id;
            return linkProfileId === profileId;
          })
          .map((l) => (typeof l.application === 'string' ? l.application : (l.application?._id)));
        const allowedAppNames = apps.filter(a => allowedAppIds.includes(a._id)).map(a => a.name);
        setAllowedLinks(allowedAppNames);
      } catch (e) {
        console.error('Erro ao carregar permissões', e);
        setLastLoginDisplay(
          new Date().toLocaleString('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'medium'
          })
        );
        setAllowedLinks([]);
      } finally {
        // marca fim do carregamento mesmo em erro
        setLoading(false);
      }
    })();
  }, [userId]);

  // Agrupa os tiles por módulo
  const grouped = useMemo(() => {
    const g = new Map<string, Tile[]>();
    for (const t of TILES) {
      if (!g.has(t.module)) g.set(t.module, []);
      g.get(t.module)!.push(t);
    }
    return g;
  }, []);

  // Ordena: primeiro os módulos declarados em MODULES (com descrição), depois os que sobraram (alfabético)
  const orderedModules: ModuleMeta[] = useMemo(() => {
    const present = Array.from(grouped.keys());
    const defined = MODULES.filter(m => present.includes(m.name));
    const remaining = present
      .filter(name => !MODULES.some(m => m.name === name))
      .sort()
      .map(name => ({ name, description: '' }));
    return [...defined, ...remaining];
  }, [grouped]);

  

  if (loading) {
    return (
      <>
        <Spinner />
      </>
    );
  }

  return (
    <>

      <div className="container py-5 home-page-container">
  <h1 className="mb-1 home-greeting">
    <span className="home-greeting-home">
      <span className="uil uil-estate home-greeting-icon" aria-hidden="true"></span>
      <span className="home-greeting-home-label">Home</span>
    </span>
    <span className="home-greeting-topline">
      <span className="home-greeting-welcome">Bem-vindo,</span>
      <span className="home-greeting-name">{userFullName || 'Usuário'}</span>
    </span>
  </h1>
  <p className="home-last-login">
    Último acesso em: <span className="home-last-login-value">{lastLoginDisplay || new Date().toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' })}</span>
  </p>

        <div id="home-tiles">
      {orderedModules.map(({ name, description }) => {
    const tiles = grouped.get(name) || [];
    // calcula quais tiles desse módulo o usuário tem acesso
    const visibleTiles = tiles.filter((tile) => {
      return canAccessTile(tile.link, allowedLinks);
    });

    // se não houver nenhum tile visível, não renderiza o módulo (nem título/descrição)
    if (visibleTiles.length === 0) return null;

            return (
              <section className="module" key={name}>
                <header className="module-header">
                  <h2 className="module-title">{name}</h2>
                  {description && <p className="module-description">{description}</p>}
                </header>

                {/* CONTÊINER FLEX do módulo: os tiles ficam lado a lado, quebrando com o zoom */}
                <div className="tiles">
                  {visibleTiles.map((tile) => {
                    const tileContent = (
                      <div className="card-body">
                        {tile.icon === '/assets/img/icons/users' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/users-dark.svg' : '/assets/img/icons/users-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/profiles' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/profiles-dark.svg' : '/assets/img/icons/profiles-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/connections' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/connections-dark.svg' : '/assets/img/icons/connections-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/settings' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/settings-dark.svg' : '/assets/img/icons/settings-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/job-monitor' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/job-monitor-dark.svg' : '/assets/img/icons/job-monitor-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/globalclients' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/globalclients-dark.svg' : '/assets/img/icons/globalclients-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/chatbot' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/chatbot-dark.svg' : '/assets/img/icons/chatbot-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/sap-logs' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/sap-logs-dark.svg' : '/assets/img/icons/sap-logs-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/access-classifier' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/access-classifier-dark.svg' : '/assets/img/icons/access-classifier-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/benchmark-perfis-sap' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/benchmark-perfis-sap-dark.svg' : '/assets/img/icons/benchmark-perfis-sap-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/action-risk' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/action-risk-dark.svg' : '/assets/img/icons/action-risk-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/action-orglevel' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/action-orglevel-dark.svg' : '/assets/img/icons/action-orglevel-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/profilegen' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/profilegen-dark.svg' : '/assets/img/icons/profilegen-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/log_analyzer' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/log_analyzer-dark.svg' : '/assets/img/icons/log_analyzer-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/cowner-admin-clientes' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/cowner-admin-clientes-dark.svg' : '/assets/img/icons/cowner-admin-clientes-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/cowner-cockpit' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/cowner-cockpit-dark.svg' : '/assets/img/icons/cowner-cockpit-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : tile.icon === '/assets/img/icons/sap-controls' ? (
                          <img src={theme === 'dark' ? '/assets/img/icons/sap-controls-dark.svg' : '/assets/img/icons/sap-controls-light.svg'} alt="" aria-hidden="true" className="tile-icon" />
                        ) : (
                          <img src={tile.icon} alt="" aria-hidden="true" className="tile-icon" />
                        )}
                        <h5 className="card-title tile-title" style={{ fontSize: '1.05rem' }} title={tile.title}>{tile.title}</h5>
                        <p className="card-text tile-text" style={{ fontSize: '0.75rem' }} title={tile.description}>{tile.description}</p>
                      </div>
                    );

                    if (isSpaLink(tile.link)) {
                      return (
                        <Link
                          key={`${name}:${tile.title}`}
                          to={tile.link}
                          className="card tile text-decoration-none"
                          aria-label={`${name} - ${tile.title}`}
                        >
                          {tileContent}
                        </Link>
                      );
                    }

                    return (
                      <a
                        key={`${name}:${tile.title}`}
                        href={tile.link}
                        className="card tile text-decoration-none"
                        aria-label={`${name} - ${tile.title}`}
                      >
                        {tileContent}
                      </a>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </>
  );

};

export default Home;
